import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline as SuccessIcon,
  ErrorOutline as ErrorIcon,
  SwapHoriz as RenameIcon,
} from "@mui/icons-material";
import type { CopyEntityType, CopyOperationResult } from "../types";

interface CopySummaryModalProps {
  open: boolean;
  result: CopyOperationResult | null;
  onClose: () => void;
}

const entityLabel = (t: CopyEntityType): string =>
  t === "agent" ? "agents" : t === "cms_page" ? "CMS pages" : t === "llm_model" ? "LLM models" : "files";

const ENTITY_WARNINGS: Record<CopyEntityType, React.ReactNode> = {
  agent: (
    <>
      Agents were copied <strong>without knowledge base links</strong>. After
      creating knowledge bases in this portal, re-link them on each agent.
    </>
  ),
  cms_page: (
    <>
      CMS pages were copied <strong>without agent embeds</strong>. Pages that
      were restricted by roles in the source portal are now{" "}
      <strong>hidden from end-users</strong> until you assign roles in this
      portal (see Roles & Permissions). Pages that were public in the source
      remain public.
    </>
  ),
  file: (
    <>
      Files were copied as-is. References to these files from CMS pages or
      agents must be reconfigured manually.
    </>
  ),
  llm_model: (
    <>
      LLM models were assigned to the portal. The <strong>default model</strong>{" "}
      was not copied — set it manually on the LLM Models tab.
    </>
  ),
};

const GLOBAL_REMINDERS = (
  <>
    <Typography variant="body2" sx={{ mb: 0.5 }}>
      Remember — these are <strong>not</strong> copied and must be set up
      manually in this portal:
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      <Typography component="li" variant="body2">
        Knowledge bases (re-create and re-upload documents)
      </Typography>
      <Typography component="li" variant="body2">
        Roles & permissions (re-create and assign users)
      </Typography>
      <Typography component="li" variant="body2">
        SSO providers, SMTP credentials, custom domains, external API keys
      </Typography>
    </Box>
  </>
);

const CopySummaryModal: React.FC<CopySummaryModalProps> = ({
  open,
  result,
  onClose,
}) => {
  if (!result) return null;

  const { succeeded, failed, entity_type } = result;
  const renamedCount = succeeded.filter((s) => s.renamed).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Import summary — {entityLabel(entity_type)}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1}>
            <Chip
              icon={<SuccessIcon fontSize="small" />}
              label={`${succeeded.length} copied`}
              color="success"
              size="small"
            />
            {failed.length > 0 && (
              <Chip
                icon={<ErrorIcon fontSize="small" />}
                label={`${failed.length} failed`}
                color="error"
                size="small"
              />
            )}
            {renamedCount > 0 && (
              <Chip
                icon={<RenameIcon fontSize="small" />}
                label={`${renamedCount} renamed`}
                color="warning"
                size="small"
              />
            )}
          </Stack>

          {succeeded.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Copied successfully
              </Typography>
              <Stack spacing={0.5}>
                {succeeded.map((s) => (
                  <Typography key={s.source_id} variant="body2">
                    • {s.source_name}
                    {s.renamed && (
                      <Typography
                        component="span"
                        variant="body2"
                        color="warning.main"
                        sx={{ ml: 1 }}
                      >
                        → {s.dest_name} (renamed, conflict)
                      </Typography>
                    )}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {failed.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Failed to copy
              </Typography>
              <Stack spacing={0.5}>
                {failed.map((f) => (
                  <Typography key={f.source_id} variant="body2">
                    •{" "}
                    <Typography component="span" fontWeight={600} variant="body2">
                      {f.source_name}
                    </Typography>{" "}
                    —{" "}
                    <Typography component="span" color="error.main" variant="body2">
                      {f.error_code}
                    </Typography>
                    : {f.error_message}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          <Divider />

          <Alert severity="warning">{ENTITY_WARNINGS[entity_type]}</Alert>
          <Alert severity="info">{GLOBAL_REMINDERS}</Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopySummaryModal;
